import React from 'react';
import { Camera, Settings, Info, Calendar, Filter, Layers } from 'lucide-react';

/**
 * Компонент для отображения детальной информации о камере и настройках съемки
 */
const ExifDetailsPanel = ({ exifData, section = 'camera' }) => {
  if (!exifData) return null;

  // Функция для форматирования даты
  const formatDate = (dateString) => {
    if (!dateString) return 'Нет данных';
    const date = new Date(dateString);
    return date.toLocaleString('ru-RU', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit'
    });
  };

  return (
    <div className="bg-white dark:bg-gray-800 rounded-xl shadow-md border border-gray-200 dark:border-gray-700 p-6">
      <h2 className="text-xl font-semibold text-gray-900 dark:text-white mb-6 flex items-center">
        <Camera className="w-5 h-5 mr-2 text-purple-500" />
        Информация о камере и настройках съемки
      </h2>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Информация о камере */}
        <div className="bg-gray-50 dark:bg-gray-900/50 rounded-lg p-5 border border-gray-200 dark:border-gray-700">
          <h3 className="text-base font-medium text-gray-900 dark:text-white mb-4 flex items-center">
            <Camera className="w-4 h-4 mr-2 text-purple-500" />
            Камера
          </h3>
          
          <div className="space-y-3">
            <DataItem 
              label="Производитель" 
              value={exifData.camera.make || 'Нет данных'} 
            />
            <DataItem 
              label="Модель" 
              value={exifData.camera.model || 'Нет данных'} 
            />
            <DataItem 
              label="Объектив" 
              value={exifData.camera.lensModel || 'Нет данных'} 
            />
            {exifData.camera.lensSerialNumber && (
              <DataItem 
                label="Серийный номер объектива" 
                value={exifData.camera.lensSerialNumber} 
              />
            )}
            <DataItem 
              label="Программное обеспечение" 
              value={exifData.camera.software || 'Нет данных'} 
            />
          </div>

          {/* Информационный блок о российских камерах */}
          {exifData.camera.make === 'Зенит' && (
            <div className="mt-4 bg-blue-50 dark:bg-blue-900/20 rounded-lg p-3 text-xs text-blue-700 dark:text-blue-300">
              <p className="font-medium mb-1">Отечественная камера</p>
              <p>
                Красногорский завод им. С.А. Зверева (КМЗ) - историческое российское предприятие, выпускающее фотоаппараты Зенит с 1952 года.
              </p>
            </div>
          )}
        </div>

        {/* Настройки съемки */}
        <div className="bg-gray-50 dark:bg-gray-900/50 rounded-lg p-5 border border-gray-200 dark:border-gray-700">
          <h3 className="text-base font-medium text-gray-900 dark:text-white mb-4 flex items-center">
            <Settings className="w-4 h-4 mr-2 text-purple-500" />
            Настройки съемки
          </h3>
          
          <div className="space-y-3">
            <DataItem 
              label="Диафрагма" 
              value={`f/${exifData.settings.aperture}`} 
            />
            <DataItem 
              label="Выдержка" 
              value={exifData.settings.shutterSpeed} 
            />
            <DataItem 
              label="ISO" 
              value={exifData.settings.iso.toString()} 
            />
            <DataItem 
              label="Фокусное расстояние" 
              value={`${exifData.settings.focalLength} мм`} 
            />
            {exifData.settings.focalLength35mm && (
              <DataItem 
                label="Эквивалент 35мм" 
                value={`${exifData.settings.focalLength35mm} мм`} 
              />
            )}
            <DataItem 
              label="Экспокоррекция" 
              value={`${exifData.settings.exposureCompensation} EV`} 
            />
          </div>
        </div>

        {/* Дополнительные настройки */}
        <div className="bg-gray-50 dark:bg-gray-900/50 rounded-lg p-5 border border-gray-200 dark:border-gray-700">
          <h3 className="text-base font-medium text-gray-900 dark:text-white mb-4 flex items-center">
            <Filter className="w-4 h-4 mr-2 text-purple-500" />
            Дополнительные настройки
          </h3>
          
          <div className="space-y-3">
            <DataItem 
              label="Режим съемки" 
              value={exifData.settings.exposureProgram} 
            />
            <DataItem 
              label="Баланс белого" 
              value={exifData.settings.whiteBalance} 
            />
            <DataItem 
              label="Режим замера" 
              value={exifData.settings.meteringMode} 
            />
            <DataItem 
              label="Ориентация" 
              value={exifData.image.orientation} 
            />
            <DataItem 
              label="Цветовое пространство" 
              value={exifData.image.colorSpace} 
            />
          </div>
        </div>
      </div>

      {/* Временная информация */}
      <div className="mt-6 bg-gray-50 dark:bg-gray-900/50 rounded-lg p-5 border border-gray-200 dark:border-gray-700">
        <h3 className="text-base font-medium text-gray-900 dark:text-white mb-4 flex items-center">
          <Calendar className="w-4 h-4 mr-2 text-purple-500" />
          Временная информация
        </h3>
        
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <DataItem 
            label="Дата съемки" 
            value={formatDate(exifData.date.original)} 
          />
          <DataItem 
            label="Дата оцифровки" 
            value={formatDate(exifData.date.digitized)} 
          />
          <DataItem 
            label="Дата изменения" 
            value={formatDate(exifData.date.modified)} 
          />
        </div>
      </div>

      {/* Информация о форматировании */}
      <div className="mt-6 bg-gray-50 dark:bg-gray-900/50 rounded-lg p-5 border border-gray-200 dark:border-gray-700">
        <h3 className="text-base font-medium text-gray-900 dark:text-white mb-4 flex items-center">
          <Layers className="w-4 h-4 mr-2 text-purple-500" />
          Информация об изображении
        </h3>
        
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <DataItem 
            label="Разрешение" 
            value={`${exifData.image.width} × ${exifData.image.height} пикселей`} 
          />
          <DataItem 
            label="Формат" 
            value={exifData.image.format} 
          />
          <DataItem 
            label="Компрессия" 
            value={exifData.image.compression} 
          />
          <DataItem 
            label="Битовая глубина" 
            value={`${exifData.image.bitDepth} бит`} 
          />
        </div>
      </div>

      {/* Авторские права и метаданные */}
      {(exifData.copyright || exifData.author || exifData.description) && (
        <div className="mt-6 bg-gray-50 dark:bg-gray-900/50 rounded-lg p-5 border border-gray-200 dark:border-gray-700">
          <h3 className="text-base font-medium text-gray-900 dark:text-white mb-4 flex items-center">
            <Info className="w-4 h-4 mr-2 text-purple-500" />
            Авторские права и метаданные
          </h3>
          
          <div className="space-y-3">
            {exifData.author && (
              <DataItem 
                label="Автор" 
                value={exifData.author} 
              />
            )}
            {exifData.copyright && (
              <DataItem 
                label="Авторские права" 
                value={exifData.copyright} 
              />
            )}
            {exifData.description && (
              <DataItem 
                label="Описание" 
                value={exifData.description} 
              />
            )}
          </div>
        </div>
      )}

      {/* Разъяснение о российских особенностях */}
      <div className="mt-6 p-4 bg-gray-100 dark:bg-gray-700/40 rounded-lg text-sm text-gray-600 dark:text-gray-400">
        <p className="mb-2 font-medium">Соответствие российскому законодательству:</p>
        <p>
          В России защита авторских прав на фотографии регулируется Гражданским кодексом РФ (Часть 4). 
          Срок охраны авторских прав составляет время жизни автора плюс 70 лет. EXIF-метаданные могут 
          служить дополнительным подтверждением авторства в случае споров.
        </p>
      </div>
    </div>
  );
};

// Компонент элемента данных
const DataItem = ({ label, value }) => (
  <div className="flex flex-col">
    <span className="text-xs text-gray-500 dark:text-gray-400">
      {label}
    </span>
    <span className="text-sm font-medium text-gray-900 dark:text-white">
      {value || 'Нет данных'}
    </span>
  </div>
);

export default ExifDetailsPanel;